import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly
import { Helmet } from "react-helmet-async";

const Candidature = () => {
  const [formData, setFormData] = useState({
    offre_id: "",
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    cv_fichier: null,
    lettre_motivation: null,
    quartier: "",
  });

  const [offres, setOffres] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [offresLoading, setOffresLoading] = useState(true);
  const [offresError, setOffresError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOffres = async () => {
      try {
        setOffresLoading(true);
        setOffresError(null);
        // Use axios directly for CancelToken
        const source = axios.CancelToken.source();
        const timeoutId = setTimeout(
          () => source.cancel("Request timeout"),
          10000,
        );
        const response = await apiClient.get("/offres_emploi", {
          cancelToken: source.token,
          timeout: 10000,
        });
        clearTimeout(timeoutId);
        if (
          response.data &&
          (response.data.offres_emploi || Array.isArray(response.data))
        ) {
          setOffres(response.data.offres_emploi || response.data);
        } else {
          throw new Error("Invalid data format received");
        }
      } catch (error) {
        // Use axios.isCancel directly
        if (axios.isCancel(error)) {
          setOffresError("Délai dépassé: Impossible de charger les offres");
        } else if (error.response) {
          setOffresError(`Erreur serveur: ${error.response.status}`);
        } else if (error.request) {
          setOffresError("Erreur réseau: Impossible de charger les offres");
        } else {
          setOffresError("Erreur inconnue lors du chargement des offres");
        }
        console.error("Erreur lors du chargement des offres", error);
      } finally {
        setOffresLoading(false);
      }
    };
    fetchOffres();
  }, [retryCount]);

  const validateForm = () => {
    const errors = {};
    if (!formData.offre_id) errors.offre_id = "Veuillez sélectionner une offre";
    if (!formData.nom.trim()) errors.nom = "Le nom est obligatoire";
    if (!formData.prenom.trim()) errors.prenom = "Le prénom est obligatoire";
    if (!formData.email.trim()) {
      errors.email = "L'email est obligatoire";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Format d'email invalide";
    }
    if (!formData.telephone.trim()) {
      errors.telephone = "Le téléphone est obligatoire";
    } else if (!/^[+]?[0-9\s\-\(\)]{8,}$/.test(formData.telephone)) {
      errors.telephone = "Format de téléphone invalide";
    }
    if (!formData.lettre_motivation) {
      errors.lettre_motivation = "La lettre de motivation est obligatoire";
    } else {
      const file = formData.lettre_motivation;
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        errors.lettre_motivation =
          "Format de fichier non supporté (PDF, DOC, DOCX uniquement)";
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.lettre_motivation = "Le fichier est trop volumineux (max 5MB)";
      }
    }
    if (formData.cv_fichier) {
      const file = formData.cv_fichier;
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(file.type)) {
        errors.cv_fichier =
          "Format de fichier non supporté (PDF, DOC, DOCX uniquement)";
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.cv_fichier = "Le fichier est trop volumineux (max 5MB)";
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files && files.length > 0) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setErrorMessage("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }
    setLoading(true);
    setErrorMessage("");
    let timeoutId;
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== "") {
          submitData.append(key, formData[key]);
        }
      });
      submitData.append("date_candidature", new Date().toISOString());
      submitData.append("statut", "Reçue");

      // Use axios directly for CancelToken
      const source = axios.CancelToken.source();
      timeoutId = setTimeout(() => source.cancel("Request timeout"), 30000);

      const response = await apiClient.post("/candidatures", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
        cancelToken: source.token,
        timeout: 30000,
      });
      clearTimeout(timeoutId);

      setSuccessMessage(
        response.data.message ||
          "Votre candidature a été envoyée avec succès ! Nous vous recontacterons rapidement.",
      );

      setFormData({
        offre_id: "",
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        cv_fichier: null,
        lettre_motivation: null,
        quartier: "",
      });
      setFieldErrors({});

      setTimeout(() => {
        setSuccessMessage("");
        navigate("/carrieres");
      }, 5000);
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      let errorMsg = "Erreur lors de la soumission de la candidature.";
      // Use axios.isCancel directly
      if (axios.isCancel(error)) {
        errorMsg =
          "Délai d'attente dépassé. Le fichier est peut-être trop volumineux.";
      } else if (error.response) {
        if (error.response.status === 422) {
          errorMsg =
            "Données invalides. Veuillez vérifier les informations saisies.";
        } else if (error.response.status >= 500) {
          errorMsg =
            "Erreur serveur. Veuillez réessayer dans quelques instants.";
        } else {
          errorMsg =
            error.response.data?.message || `Erreur ${error.response.status}`;
        }
      } else if (error.request) {
        errorMsg =
          "Problème de connexion. Vérifiez votre connexion internet et réessayez.";
      }
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryOffres = () => {
    setRetryCount((prev) => prev + 1);
  };

  const selectedOffre = offres.find((o) => o.id === formData.offre_id);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "JobApplication",
    applicant: {
      "@type": "Person",
      name: `${formData.nom} ${formData.prenom}`,
      email: formData.email,
      telephone: formData.telephone,
    },
    jobTitle: selectedOffre?.titre || "",
    dateApplied: new Date().toISOString(),
    applicationStatus: "Pending",
  };

  return (
    <div>
      <Helmet>
        <title>Candidature | DVELGE GROUP</title>
        <meta
          name="description"
          content="Postulez aux offres d'emploi chez DVELGE GROUP et rejoignez notre équipe."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <div className="contact-head-sec r-bg-a pt85 pb120">
        <div className="container">
          <div className="row pt80">
            <div className="col-lg-5 vcenter">
              <div className="page-headings">
                <span className="sub-heading mb15">
                  <i className="fas fa-briefcase mr5"></i> Candidature
                </span>
                <h1 className="mb15">Rejoignez notre équipe</h1>
                <p>
                  Remplissez le formulaire pour postuler à nos offres et nous
                  rejoindre.
                </p>
                {offresError && (
                  <div className="alert alert-warning mt-3" role="alert">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                      {offresError}{" "}
                      <button
                        className="btn btn-sm btn-outline-primary ms-2"
                        onClick={handleRetryOffres}
                        disabled={offresLoading}
                      >
                        {offresLoading ? "Chargement..." : "Réessayer"}
                      </button>
                    </small>
                  </div>
                )}
              </div>
            </div>

            <div className="col-lg-7 vcenter">
              <div className="row">
                <div className="col-lg-6 m-mt30">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-phone-alt"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Département RH</span>
                        <a href="tel:+224627020871">+224 627 02 08 71</a>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 m-mt30">
                  <div className="contact-details-block">
                    <div className="ree-row-set">
                      <div className="ree-icon-set dtb-icon">
                        <i className="fas fa-envelope"></i>
                      </div>
                      <div className="ree-details-set dtb-details">
                        <span>Recrutement</span>
                        <a href="mailto:rh@dvelgegroup.com">
                          rh@dvelgegroup.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="contact-form-sec sec-pad r-bg-d">
        <div className="container">
          <div className="row flx-end">
            <div className="col-lg-6">
              <div className="sec-heading m-center">
                <span className="sub-heading mb15">CANDIDATURE</span>
                <h2 className="mb15">
                  <span className="ree-text rt40">Votre opportunité</span> nous
                  intéresse
                </h2>
                <p>
                  Remplissez le formulaire avec précision pour postuler à une
                  offre.
                </p>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="form-contact-hom m-mt60">
                <div className="form-block bg-w">
                  <div className="form-head">
                    <h4>Postulez et rejoignez notre équipe :</h4>
                  </div>
                  <div className="form-body">
                    {successMessage && (
                      <div className="alert alert-success d-flex align-items-center">
                        <i className="fas fa-check-circle me-2"></i>{" "}
                        {successMessage}
                        <button
                          type="button"
                          className="btn-close ms-auto"
                          onClick={() => setSuccessMessage("")}
                          aria-label="Fermer"
                        />
                      </div>
                    )}

                    {errorMessage && (
                      <div className="alert alert-danger d-flex align-items-center">
                        <i className="fas fa-exclamation-triangle me-2"></i>{" "}
                        {errorMessage}
                        <button
                          type="button"
                          className="btn-close ms-auto"
                          onClick={() => setErrorMessage("")}
                          aria-label="Fermer"
                        />
                      </div>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                      {/* All inputs, selects, file uploads and validation messages */}
                      {/* Name */}
                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <input
                            type="text"
                            placeholder="Nom *"
                            name="nom"
                            value={formData.nom}
                            onChange={handleChange}
                            className={fieldErrors.nom ? "is-invalid" : ""}
                          />
                          {fieldErrors.nom && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.nom}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            placeholder="Prénom *"
                            name="prenom"
                            value={formData.prenom}
                            onChange={handleChange}
                            className={fieldErrors.prenom ? "is-invalid" : ""}
                          />
                          {fieldErrors.prenom && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.prenom}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Email and Telephone */}
                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <input
                            type="email"
                            placeholder="Adresse e-mail *"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={fieldErrors.email ? "is-invalid" : ""}
                          />
                          {fieldErrors.email && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.email}
                            </div>
                          )}
                        </div>
                        <div className="col-md-6">
                          <input
                            type="tel"
                            placeholder="Numéro de contact *"
                            name="telephone"
                            value={formData.telephone}
                            onChange={handleChange}
                            className={
                              fieldErrors.telephone ? "is-invalid" : ""
                            }
                          />
                          {fieldErrors.telephone && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.telephone}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Quartier and Offre */}
                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <input
                            type="text"
                            placeholder="Votre Quartier"
                            name="quartier"
                            value={formData.quartier}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-6">
                          <select
                            name="offre_id"
                            value={formData.offre_id}
                            onChange={handleChange}
                            className={fieldErrors.offre_id ? "is-invalid" : ""}
                            disabled={offresLoading || offresError}
                          >
                            <option value="">Sélectionnez une offre *</option>
                            {offresLoading ? (
                              <option value="" disabled>
                                Chargement des offres...
                              </option>
                            ) : offresError ? (
                              <option value="" disabled>
                                Erreur de chargement
                              </option>
                            ) : offres.length === 0 ? (
                              <option value="" disabled>
                                Aucune offre disponible
                              </option>
                            ) : (
                              offres.map((offre) => (
                                <option key={offre.id} value={offre.id}>
                                  {offre.titre}
                                </option>
                              ))
                            )}
                          </select>
                          {fieldErrors.offre_id && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.offre_id}
                            </div>
                          )}
                          {offresError && !fieldErrors.offre_id && (
                            <div className="text-warning small mt-1">
                              <i className="fas fa-exclamation-triangle me-1"></i>{" "}
                              {offresError}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Lettre de motivation and CV */}
                      <div className="fieldsets row">
                        <div className="col-md-6">
                          <label className="form-label">
                            Lettre de motivation * (PDF, DOC, DOCX - max 5MB)
                          </label>
                          <input
                            type="file"
                            name="lettre_motivation"
                            onChange={handleChange}
                            className={
                              fieldErrors.lettre_motivation ? "is-invalid" : ""
                            }
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          />
                          {fieldErrors.lettre_motivation && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.lettre_motivation}
                            </div>
                          )}
                          {formData.lettre_motivation &&
                            !fieldErrors.lettre_motivation && (
                              <div className="text-success small mt-1">
                                <i className="fas fa-check me-1"></i> Fichier
                                sélectionné: {formData.lettre_motivation.name}
                              </div>
                            )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">
                            CV (PDF, DOC, DOCX - max 5MB)
                          </label>
                          <input
                            type="file"
                            name="cv_fichier"
                            onChange={handleChange}
                            className={
                              fieldErrors.cv_fichier ? "is-invalid" : ""
                            }
                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          />
                          {fieldErrors.cv_fichier && (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.cv_fichier}
                            </div>
                          )}
                          {formData.cv_fichier && !fieldErrors.cv_fichier && (
                            <div className="text-success small mt-1">
                              <i className="fas fa-check me-1"></i> Fichier
                              sélectionné: {formData.cv_fichier.name}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Submit button */}
                      <div className="fieldsets mt20">
                        <button
                          type="submit"
                          className="ree-btn ree-btn-grdt1 w-100"
                          disabled={loading || offresLoading}
                        >
                          {loading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              Envoyer votre candidature{" "}
                              <i className="fas fa-arrow-right fa-btn"></i>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="fieldsets mt-3">
                        <p className="text-center text-muted small">
                          * Champs obligatoires
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Candidature;
